export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_RECOMMENDED_LENGTH = 12;

export type PasswordPolicyReason =
    | 'too_short'
    | 'leading_or_trailing_space'
    | 'common_password'
    | 'simple_sequence'
    | 'contains_email'
    | 'contains_personal_info';

export interface PasswordPolicyContext {
    email?: string | null;
    personalTerms?: Array<string | null | undefined>;
}

export interface PasswordPolicyResult {
    isValid: boolean;
    reasons: PasswordPolicyReason[];
    strength: 'empty' | 'weak' | 'okay' | 'good' | 'strong';
}

const COMMON_WEAK_PASSWORDS = new Set([
    'password',
    'password1',
    'password12',
    'password123',
    'password1234',
    'admin',
    'admin123',
    'welcome',
    'welcome1',
    'welcome123',
    'letmein',
    'qwerty',
    'qwerty123',
    'abc123',
    '12345678',
    '123456789',
    '1234567890',
    '11111111',
    '00000000',
    'iloveyou',
    'monkey',
    'dragon',
    'bustly',
    'bustly123',
]);

const SIMPLE_PATTERNS = [
    '0123456789',
    '1234567890',
    'abcdefghijklmnopqrstuvwxyz',
    'qwertyuiop',
    'asdfghjkl',
    'zxcvbnm',
];

const normalizeComparable = (value: string): string => (
    value.toLowerCase().replace(/[^a-z0-9]/g, '')
);

const getEmailLocalPart = (email?: string | null): string => {
    const localPart = email?.split('@')[0] || '';
    return normalizeComparable(localPart);
};

const hasRepeatedCharacterRun = (value: string): boolean => /(.)\1{5,}/.test(value);

const hasSimpleSequence = (value: string): boolean => {
    const normalized = normalizeComparable(value);
    if (normalized.length < 6) return false;

    if (/^\d+$/.test(normalized) && normalized.length < PASSWORD_RECOMMENDED_LENGTH) {
        return true;
    }

    if (hasRepeatedCharacterRun(normalized)) {
        return true;
    }

    return SIMPLE_PATTERNS.some((pattern) => {
        for (let length = 6; length <= Math.min(pattern.length, normalized.length); length += 1) {
            for (let index = 0; index <= pattern.length - length; index += 1) {
                const sequence = pattern.slice(index, index + length);
                const reversed = sequence.split('').reverse().join('');
                if (normalized.includes(sequence) || normalized.includes(reversed)) {
                    return true;
                }
            }
        }
        return false;
    });
};

const containsContextTerm = (password: string, terms: string[]): boolean => {
    const normalizedPassword = normalizeComparable(password);
    return terms.some((term) => {
        const normalizedTerm = normalizeComparable(term);
        return normalizedTerm.length >= 4 && normalizedPassword.includes(normalizedTerm);
    });
};

const getStrength = (password: string, reasons: PasswordPolicyReason[]): PasswordPolicyResult['strength'] => {
    if (!password) return 'empty';
    if (reasons.length > 0) return 'weak';

    const hasLetter = /[a-z]/i.test(password);
    const hasDigit = /\d/.test(password);
    const hasSymbol = /[^a-z0-9]/i.test(password);
    const varietyScore = [hasLetter, hasDigit, hasSymbol].filter(Boolean).length;

    if (password.length >= 16 && varietyScore >= 2) return 'strong';
    if (password.length >= PASSWORD_RECOMMENDED_LENGTH) return 'good';
    return 'okay';
};

export const evaluatePasswordPolicy = (
    password: string,
    context: PasswordPolicyContext = {},
): PasswordPolicyResult => {
    const reasons: PasswordPolicyReason[] = [];
    const comparablePassword = normalizeComparable(password);
    const emailLocalPart = getEmailLocalPart(context.email);
    const personalTerms = (context.personalTerms || [])
        .filter((term): term is string => Boolean(term?.trim()));

    if (password.length < PASSWORD_MIN_LENGTH) {
        reasons.push('too_short');
    }

    if (password && password.trim() !== password) {
        reasons.push('leading_or_trailing_space');
    }

    if (COMMON_WEAK_PASSWORDS.has(password.toLowerCase()) || COMMON_WEAK_PASSWORDS.has(comparablePassword)) {
        reasons.push('common_password');
    }

    if (hasSimpleSequence(password)) {
        reasons.push('simple_sequence');
    }

    if (emailLocalPart.length >= 4 && comparablePassword.includes(emailLocalPart)) {
        reasons.push('contains_email');
    }

    if (containsContextTerm(password, personalTerms)) {
        reasons.push('contains_personal_info');
    }

    return {
        isValid: reasons.length === 0,
        reasons,
        strength: getStrength(password, reasons),
    };
};

export const getPrimaryPasswordPolicyReason = (
    result: PasswordPolicyResult,
): PasswordPolicyReason | null => result.reasons[0] || null;
